import { PromotionInputs } from '@/pages/Promotions/PromotionModal/PromotionModal';
import { ApiPromotion } from '@api/interfaces';
import { RewardsType } from '@shared/enums/RewardsType';
import {
  businessToday,
  findPromotionProgramPeriod,
  PromotionProgramPeriodError,
} from '@shared/utilities';
import { isEqual, isEqualWith, isNil, isUndefined, omitBy, round } from 'lodash';

const isEqualIgnoringArrayOrder = <T>(a: T, b: T) => isEqualWith(a, b, (x, y) => (
  Array.isArray(x) && Array.isArray(y)
    ? isEqual([...x].sort(), [...y].sort())
    : undefined
));

export const getSelectedLocationsText = (selected: number, total: number) => {
  if (selected === total) {
    return `${selected} location${selected === 1 ? '' : 's'}`;
  }
  return `${selected} of ${total} locations`;
};

export const isEndDateInPast = ({ endDate }: PromotionInputs) => (
  !!endDate && endDate < businessToday()
);

export type ProgramPeriodCheck =
  | { status: 'notChecked' }
  | { status: 'pending' }
  | { error?: PromotionProgramPeriodError, status: 'checked' };

interface ProgramPeriodCheckInputs {
  isProgramCreationDisabled?: boolean,
  // The retailer's active and future programs; undefined while they load or if loading failed.
  programs?: { endDate: string, id: string, startDate: string }[],
  promotion: PromotionInputs,
  // The saved end date when editing; the API re-checks the program period only if it changes.
  storedEndDate?: string,
}

// Mirrors the API: PromotionService.getProgramId runs the same findPromotionProgramPeriod.
export const checkProgramPeriod = ({
  isProgramCreationDisabled,
  programs,
  promotion: { endDate, startDate },
  storedEndDate,
}: ProgramPeriodCheckInputs): ProgramPeriodCheck => {
  const isEndDateUnchanged = storedEndDate !== undefined && endDate === storedEndDate;
  if (!startDate || !endDate || isProgramCreationDisabled || isEndDateUnchanged) {
    return { status: 'notChecked' };
  }
  if (!programs) {
    return { status: 'pending' };
  }
  const today = businessToday();
  const { error } = findPromotionProgramPeriod(
    programs.map((program) => ({ ...program, isFuture: program.startDate > today })),
    startDate,
    endDate,
  );
  return { error, status: 'checked' };
};

export const isProgramPeriodBlocking = (check: ProgramPeriodCheck) => (
  check.status === 'pending' || (check.status === 'checked' && !!check.error)
);

export const isPointCapBelowPointsAwarded = (
  promotion: PromotionInputs,
  storedPointCap?: number | null,
) => (
  !isNil(promotion.pointCap)
  && !isNil(promotion.pointsAwarded)
  && promotion.pointCap !== storedPointCap
  && promotion.pointCap < promotion.pointsAwarded
);

export const isPointsExpirationBeforeEndDate = (
  { endDate, pointsExpirationDate }: PromotionInputs,
) => (
  !!endDate && !!pointsExpirationDate && pointsExpirationDate < endDate
);

export const isPointsExpirationInPast = ({ pointsExpirationDate }: PromotionInputs) => (
  !!pointsExpirationDate && pointsExpirationDate < businessToday()
);

// An edited promotion may clear its redeem list; a new "select products" promotion needs one.
export const hasRequiredRedeemProducts = (
  { productIdsForRedeemingPoints, redeemOnAllProducts }: PromotionInputs,
  isEditMode: boolean,
) => (
  !!productIdsForRedeemingPoints
  && (isEditMode || !!redeemOnAllProducts || productIdsForRedeemingPoints.length > 0)
);

export const isPromotionDateRangeInverted =({ endDate, startDate }: PromotionInputs) => (
  !!startDate && !!endDate && startDate > endDate
);

export const omitUnchangedFields = <T extends Partial<ApiPromotion>>(
  updates: T,
  storedPromotion?: ApiPromotion,
): Partial<T> => {
  if (!storedPromotion) {
    return updates;
  }
  return Object.fromEntries(
    Object.entries(updates).filter(([field, value]) => (
      !isEqualIgnoringArrayOrder(value, storedPromotion[field as keyof ApiPromotion])
    )),
  ) as Partial<T>;
};

export const hasPromotionInputChanges = (
  current: PromotionInputs,
  original: PromotionInputs,
) => !isEqualIgnoringArrayOrder(omitBy(current, isUndefined), omitBy(original, isUndefined));

export const toPromotionInputs = (promotion: ApiPromotion): PromotionInputs => ({
  ...promotion,
  manufacturerId: promotion.manufacturerId ?? '',
  redeemOnAllProducts: !promotion.productIdsForRedeemingPoints.length,
  showInDashboardCarousel: promotion.showInDashboardCarousel ?? false,
  showInDashboardTable: promotion.showInDashboardTable ?? false,
  subwalletId: promotion.subwalletId ?? undefined,
  value: promotion.rewardType === RewardsType.Dollars
    ? promotion.value
    : round(promotion.value * 100, 5),
});
